import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar, Tooltip, useTheme } from "@mui/material";
import IconButtonWithTooltip from "../Shared/IconButtonWithTooltip";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import CallIcon from "@mui/icons-material/Call";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Profile2User, Call, CallCalling, CallRemove, CallSlash } from "iconsax-react";
import { nameToContactAvatarImage, NON_DRAGGABLE_AVATAR_IMG_PROPS } from "../../../config/AvatarColor";
import LifecycleStageChip from "../../Lifecycle/LifecycleStageChip";
import PlatformIcon from "../Features/PlatformIcon";
import { actionBtnSx, actionIconBoxSx } from "../../../utils/callActionButtonStyles";

// Same call-outcome vocabulary CallLogsTable.jsx uses, now theme-aware (was
// hardcoded hex — broke in dark mode) and reusing the app's semantic palette
// tokens, matching MessageCard's status-badge convention. Missed/abandoned
// moved from warning(orange) to error(red) and every outcome now gets its
// own Iconsax glyph instead of one flat Call icon everywhere — matches the
// reference mockup Rahim shared (2026-08-20): CallCalling (active/ringing
// waves) for anything still live, CallRemove for missed/no-answer, CallSlash
// for declined/failed, plain Call for a normally-completed call.
function getStatusMeta(theme, status) {
  const neutral = { label: status === "connecting" ? "Connecting" : "Ringing", tone: theme.palette.text.secondary, Icon: CallCalling };
  const success = { tone: theme.palette.success.main };
  const error = { tone: theme.palette.error.main };
  const map = {
    ringing: neutral,
    connecting: neutral,
    connected: { ...success, label: "Ongoing call", Icon: CallCalling },
    completed: { ...error, label: "Call ended", Icon: Call },
    missed: { ...error, label: "Missed call", Icon: CallRemove },
    abandoned: { ...error, label: "No answer", Icon: CallRemove },
    declined: { ...error, label: "Declined", Icon: CallSlash },
    failed: { ...error, label: "Failed", Icon: CallSlash },
  };
  return map[status] || { ...error, label: "Failed", Icon: CallSlash };
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

// Small deterministic color per agent name — same "hash the name" convention
// TasksListTable.jsx's person avatars use, kept local/minimal here rather than
// importing that file's bigger palette module for one 16px dot.
const AGENT_COLORS = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6"];
function agentColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AGENT_COLORS[Math.abs(h) % AGENT_COLORS.length];
}

// Shared with CallWidget.jsx's incoming-call popup — see callActionButtonStyles.js.

/**
 * Call row — restyled 2026-08-20 to match the reference call-log mockup
 * Rahim shared (name+time header, a colored call-icon+status+duration line,
 * an attribution line) rebuilt in ClusterDen's own components/tokens:
 * PlatformIcon for the avatar badge (same one MessageCard uses), theme
 * palette for status color instead of the reference's raw hex, and the
 * agent-avatar + LifecycleStageChip row folded into the attribution line so
 * Calls keeps the same Lifecycle/assignment affordances Chats has.
 */
export default function LiveCallCard({ call, live, answerable, onClick, onAnswer, onReject, onJoin, onOpenRinging, onLifecycleChanged }) {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const isTicking = live && call.status === "connected" && call.connectedAt;
  const isAnswerable =
    live && call.status === "ringing" && call.direction === "inbound" && answerable && Boolean(onAnswer || onReject);
  const isJoinable = live && call.bridgeMode === "sfu" && call.status === "connected" && !call.agentId && Boolean(onJoin);
  const isRecoverable =
    live && call.status === "ringing" && call.direction === "inbound" && !isAnswerable && Boolean(onOpenRinging);
  const isActionable = isAnswerable || isJoinable || isRecoverable;

  useEffect(() => {
    if (!isTicking) return undefined;
    const connectedAtMs = new Date(call.connectedAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - connectedAtMs) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isTicking, call.connectedAt]);

  const meta = getStatusMeta(theme, call.status);
  const isMissedLike = ["missed", "abandoned", "declined", "failed"].includes(call.status);
  const clickable = Boolean(call.contactId && onClick);

  const durationText = isTicking
    ? formatDuration(elapsed)
    : !live && (call.status === "completed" || call.durationSeconds > 0)
    ? formatDuration(call.durationSeconds)
    : "";

  // Attribution line — "X answered" (inbound) / "X called" (outbound) once an
  // agent owns the contact, "No answer" for the missed-like states, "Needs
  // agent" for an auto-connected call nobody's joined yet (same unassigned
  // treatment as "No answer" — Rahim, 2026-08-20), otherwise nothing (a
  // still-ringing call an agent hasn't claimed yet).
  const attributionText = call.agentName
    ? `${call.agentName} ${call.direction === "inbound" ? "answered" : "called"}`
    : isMissedLike
    ? "No answer"
    : isJoinable
    ? "Needs agent"
    : "";
  const showUnassignedIcon = !call.agentName && (isMissedLike || isJoinable);
  const showAttributionRow = Boolean(attributionText || call.lifecycleStageId);

  return (
    <Box
      onClick={() => clickable && onClick(call)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        // Same vertical breathing room AND minHeight as MessageCard's Chats
        // row (pt/pb 1.75, same responsive minHeight) — kept pinned to that
        // exact value even after widening the inner line-gaps below, per
        // Rahim's ask (2026-08-20): card height stays matched to Chats,
        // only the spacing between the 3 lines inside it grows.
        pt: 1.75,
        pb: 1.75,
        minHeight: { xs: "115px", sm: "88px", md: "115px", lg: "115px", xl: "115px" },
        // This app has no global box-sizing:border-box (recurring gotcha,
        // see MessageCard.jsx's own comment on this) — without this, the
        // pt/pb above ADD on top of minHeight instead of being absorbed by
        // it, so the row rendered ~28px taller than Chats' real 115px/88px
        // despite the matching minHeight value (Rahim caught this live,
        // 2026-08-20).
        boxSizing: "border-box",
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: clickable ? "pointer" : "default",
        transition: "background-color 0.15s ease",
        "&:hover": clickable
          ? { bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)" }
          : undefined,
      }}
    >
      {/* Avatar + platform badge — reuses MessageCard's own PlatformIcon, the
          same "colored circle pinned to the avatar's corner" every Chats row
          already uses, instead of a one-off direction-colored dot. */}
      <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
        <Avatar
          src={nameToContactAvatarImage(call.contactName)}
          imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
          sx={{
            // Matches MessageCard's own desktop avatar size (50px), not a
            // smaller one-off — same reuse-Chats-spacing ask.
            width: 50,
            height: 50,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
        {/* display:flex (not the Box default block) is load-bearing, not
            decoration — a block-level shrink-to-fit box sizes its inline-flex
            child via baseline/line-height rules, which pulls in the SURROUNDING
            font-size context. That context differs between this row's compact
            font and CallWidget's larger one, so the exact same code rendered a
            perfect circle here but an oval there — flex removes the
            font-size dependency entirely (Rahim caught it live, 2026-08-20). */}
        <Box sx={{ position: "absolute", bottom: -2, right: -2, borderRadius: "50%", border: `2px solid ${theme.palette.background.default}`, display: "flex" }}>
          {/* sx={{ml:0}} cancels PlatformIcon's own baked-in ml:0.5 default —
              MessageCard.jsx's identical badge does the same; without it the
              ring wrapper's width/height mismatch (extra 4px from the
              margin) turns the border-radius:50% ring into an oval instead
              of a circle, so the icon reads as sitting outside/off-center in
              its white ring (Rahim caught this live, 2026-08-20). */}
          <PlatformIcon platform="whatsapp" size={18} sx={{ ml: 0 }} />
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* Row 1: name — timestamp, or the live action buttons in its place.
            lineHeight:1.2 on the name (explicit, not the ~1.43-1.5 theme
            default) is the actual fix, not a magic-number nudge — the default
            leaves invisible ascent/descent padding around the glyph, so a
            button flex-centered against the untightened line-box centers on
            that padded box, not the visible text, and reads as "too high" no
            matter what offset gets thrown at it. With a tight, known
            lineHeight the row's real height is deterministic (15px font ×
            1.2 = 18px, not an empirically-measured guess), and centering the
            button against THAT height lines it up with the glyph itself
            (Rahim, 2026-08-20 — rejected the earlier mt-nudge hack). */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, height: "18px" }}>
          {/* Full name on hover — same ellipsis-truncation gap already fixed
              for ChatboxNavbar's assignee name (Rahim, 2026-08-29: "did you
              add for the name and the assigned one"). */}
          <Tooltip title={call.contactName || call.waNumber} componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.2, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {call.contactName || call.waNumber}
            </Typography>
          </Tooltip>
          {isActionable ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
              {isAnswerable && (
                <>
                  <IconButtonWithTooltip label="Decline" size="small" onClick={(e) => { e.stopPropagation(); onReject?.(call.callId); }} sx={actionBtnSx(theme.palette.error.main, false)} aria-label="Decline">
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButtonWithTooltip>
                  <IconButtonWithTooltip label="Answer" size="small" onClick={(e) => { e.stopPropagation(); onAnswer?.(call.callId); }} sx={actionBtnSx(theme.palette.success.main, true)} aria-label="Answer">
                    <Box sx={actionIconBoxSx}><Call size="100%" color="currentColor" variant="Bold" /></Box>
                  </IconButtonWithTooltip>
                </>
              )}
              {isJoinable && (
                <IconButtonWithTooltip label="Join" size="small" onClick={(e) => { e.stopPropagation(); onJoin?.(call); }} sx={actionBtnSx(theme.palette.success.main, true)} aria-label="Join">
                  <CallIcon sx={{ fontSize: 17 }} />
                </IconButtonWithTooltip>
              )}
              {isRecoverable && (
                <IconButtonWithTooltip label="Open call" size="small" onClick={(e) => { e.stopPropagation(); onOpenRinging?.(call.callId); }} sx={actionBtnSx(theme.palette.success.main, true)} aria-label="Open call">
                  <OpenInNewIcon sx={{ fontSize: 17 }} />
                </IconButtonWithTooltip>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.2, color: "text.secondary", flexShrink: 0 }}>
              {formatWhen(call.startedAt || call.createdAt)}
            </Typography>
          )}
        </Box>

        {/* Row 2: call icon + colored status label + duration, one unit */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.25 }}>
          <Box sx={{ width: 16, height: 16, display: "flex", flexShrink: 0 }}>
            <meta.Icon size="100%" color={meta.tone} variant="Bold" />
          </Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: meta.tone, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {meta.label}{durationText ? ` · ${durationText}` : ""}
          </Typography>
        </Box>

        {/* Row 3: assigned-agent avatar + attribution + Lifecycle tag — the
            two things Chats' row already surfaces (assignee, lifecycle stage)
            that Calls never had. Renders nothing when there's no agent, no
            "No answer" case, and no stage set, so short-lived ringing rows
            don't grow for an empty line. */}
        {showAttributionRow && (
          // mt is 0.25 less than row 2's (1.25) — same code value produced a
          // visibly bigger gap here (12px vs 10px, live-measured) since this
          // row's own text/icon line-box is shorter than row 2's, shifting
          // its content lower within its box. Compensated empirically to
          // match row 1→2's exact 10px gap (Rahim caught this live, 2026-08-20).
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
            {call.agentName && (
              <Tooltip title={`Assigned: ${call.agentName}`} componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                <Box
                  sx={{
                    width: 15,
                    height: 15,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    bgcolor: agentColor(call.agentName),
                    color: "#fff",
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                  }}
                >
                  {call.agentName.trim()[0]?.toUpperCase()}
                </Box>
              </Tooltip>
            )}
            {showUnassignedIcon && (
              <Tooltip title="Unassigned" componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                <Box sx={{ width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, bgcolor: theme.palette.action.selected, color: "text.secondary" }}>
                  <Profile2User size="9" variant="Linear" color="currentColor" />
                </Box>
              </Tooltip>
            )}
            {attributionText && (
              <Tooltip title={attributionText} componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {attributionText}
                </Typography>
              </Tooltip>
            )}
            {call.lifecycleStageId && (
              <LifecycleStageChip
                contactId={call.contactId}
                lifecycleStageId={call.lifecycleStageId}
                onChanged={(newStageId) => onLifecycleChanged?.(call.contactId, newStageId)}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
